import {getIsLoadingParkingReservations} from '../../../data';
import {
  getSomeWorkdaysHaveLoaded,
  getSomeWorkspaceReservationsHaveLoaded,
} from '../../../data/timeslots/selectors/index';
import {useAppSelector} from '../../../hooks';
import {FeatureMap} from '../../../infrastructure';
import {
  useAvailableRoomsRelevant,
  useCustomerBannerReady,
  useCustomerBannerRelevant,
  useEmployeeCommunicationAnouncementRelevant,
  useExtrasReady,
  useExtrasRelevant,
  useMeetingConsentTileReady,
  useMeetingConsentTileRelevant,
  useNextWorkdaysReady,
  useUpcomingMeetingsTileReady,
  useUpcomingMeetingsTileRelevant,
  useWorkdayTileRelevant,
} from '../useHomePageSections';
import {PartOfDay} from '../useHomeTime';

// useXReady -> we don't need all data, but we do need to know enough for the component to be able
//              to render at a fixed height

// Note: these could be selectors but I feel they are too tied to UI to really be reusable
const useWorkdayTileReady = () => {
  // Different sizes based on which 3 sections are available
  // If we implement a design that has the same height for all combinations, we can simply return true here.
  // For now, I think it's better to only add sections if they are relevant (e.g. don't add a map if there's
  // no workday)
  const workdayReady = useAppSelector(getSomeWorkdaysHaveLoaded);
  const workspaceReady = useAppSelector(getSomeWorkspaceReservationsHaveLoaded);
  const parkingReady = !useAppSelector(getIsLoadingParkingReservations);

  return workdayReady && workspaceReady && parkingReady;
};

const useAvailableRoomsReady = () => {
  // We only show it if we know there is an in office workday
  const workdayReady = useAppSelector(getSomeWorkdaysHaveLoaded);

  return workdayReady;
};

const useEmployeeCommunicationAnouncementReady = () => {
  // We only show it if we know there is an in office workday
  const workdayReady = useAppSelector(getSomeWorkdaysHaveLoaded);

  return workdayReady;
};

export const useHomePageSections_timeslots = (dateKey: string, partOfDay: PartOfDay, enabledFeatures: FeatureMap) => {
  const overview = [
    {
      componentKey: 'CustomerBanner',
      ready: useCustomerBannerReady(),
      relevant: useCustomerBannerRelevant(enabledFeatures),
    },
    {
      componentKey: 'EmployeeCommunicationAnouncement',
      ready: useEmployeeCommunicationAnouncementReady(),
      relevant: useEmployeeCommunicationAnouncementRelevant(enabledFeatures),
    },
    {
      componentKey: 'WorkdayTile',
      ready: useWorkdayTileReady(),
      relevant: useWorkdayTileRelevant(),
    },
    {
      componentKey: 'NextOfficeDaysTileSimplified',
      ready: useNextWorkdaysReady(),
      relevant: true,
    },

    {
      componentKey: 'UpcomingMeetingsTile',
      ready: useUpcomingMeetingsTileReady(),
      relevant: useUpcomingMeetingsTileRelevant(enabledFeatures),
    },

    {
      componentKey: 'MeetingConsentTile',
      ready: useMeetingConsentTileReady(),
      relevant: useMeetingConsentTileRelevant(enabledFeatures),
    },

    {
      componentKey: 'AvailableRoomsTile',
      ready: useAvailableRoomsReady(),
      relevant: useAvailableRoomsRelevant(dateKey, enabledFeatures) && partOfDay === 'WorkingHours',
    },

    {
      componentKey: 'ConnectionsTile',
      ready: true,
      relevant: true,
    },

    {
      componentKey: 'ExtrasTile',
      ready: useExtrasReady(),
      relevant: useExtrasRelevant(enabledFeatures),
    },
  ] as const;

  // Return up to the first that is not ready
  const firstLoadingIndex = overview.findIndex((t) => !t.ready);

  const toRender = firstLoadingIndex === -1 ? overview : overview.slice(0, firstLoadingIndex);

  return toRender.filter((r) => r.relevant);
};
